// components/ui/Tabs.jsx
// Created: 2026-07-20
// Project: Giving Words to Voices
// Version: 1.0.0
//
// The Tabs primitive — the segmented control the app uses in a dozen places,
// each hand-built. Like the buttons, the active tab set its label to a
// hardcoded white on the accent fill, so on a scheme whose accent is near-white
// the active tab's label vanished. Here the active label is contrastText of the
// fill, so it is legible on every scheme.
//
// tabs: [{ key, label, icon?, count?, disabled? }]. activeColor overrides the
// fill (defaults to the scheme highlight); pass theme.glossaryAccent etc. to
// keep an existing accent.

import React from 'react';
import { RADIUS, SPACE, WEIGHT } from '../../constants/tokens';
import { contrastText } from '../../constants/theme';
import { Icon } from '../Icon';

export default function Tabs({
  tabs = [],
  value,
  onChange,
  fill = true,          // stretch each tab to equal width (segmented look)
  activeColor,          // fill for the active tab; defaults to theme.highlight
  size = 'md',
  theme = {},
  style = {},
}) {
  const active = activeColor || theme.highlight;
  const activeText = contrastText(active);
  const font = size === 'sm' ? '0.68rem' : '0.72rem';

  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: SPACE[1],
        background: theme.card,
        borderRadius: RADIUS.md,
        padding: '3px',
        ...style,
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.key === value;
        const fg = isActive ? activeText : theme.textSub;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={tab.disabled}
            onClick={() => onChange && onChange(tab.key)}
            style={{
              flex: fill ? 1 : undefined,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: SPACE[1],
              padding: '7px',
              fontSize: font,
              fontWeight: WEIGHT.bold,
              fontFamily: 'inherit',
              background: isActive ? active : 'transparent',
              color: fg,
              border: 'none',
              borderRadius: RADIUS.sm,
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon && <Icon icon={tab.icon} size={12} color={fg} />}
            {tab.label}
            {tab.count != null && <span style={{ opacity: 0.75 }}>({tab.count})</span>}
          </button>
        );
      })}
    </div>
  );
}
